/**
 * Messages — a client's single conversation with their coach. Mobile-first
 * full-width chat (the old two-column desktop grid was unusable on phones).
 * The client only ever talks to the coach, so we drop straight into the thread;
 * sending with no active partner defaults to the coach server-side.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Conversation, Message } from '@regis/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';

export default function MessagesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const convos = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.get<{ conversations: Conversation[] }>('/api/messages/conversations'),
  });

  // The client has a single coach partner — use the first conversation if any.
  const partner = convos.data?.conversations?.[0] ?? null;
  const partnerId = partner?.partner_id ?? null;

  const thread = useQuery({
    queryKey: ['thread', partnerId],
    queryFn: () => api.get<{ messages: Message[] }>(`/api/messages/${partnerId}`),
    enabled: !!partnerId,
  });

  const send = useMutation({
    mutationFn: (content: string) =>
      api.post('/api/messages', partnerId ? { recipient_id: partnerId, content } : { content }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['conversations'] });
      if (partnerId) qc.invalidateQueries({ queryKey: ['thread', partnerId] });
    },
  });

  const messages = thread.data?.messages ?? [];

  // Local date + time for each message (device timezone — never hardcoded UTC).
  const fmtTime = (iso: string) => {
    const d = new Date(iso.includes('T') || iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z');
    const sameDay = d.toDateString() === new Date().toDateString();
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return sameDay ? time : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  return (
    <div className="flex flex-col" style={{ height: '100dvh' }}>
      <PageHeader title="Messages" subtitle={partner?.partner_name ?? 'Your coach'} />

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 flex flex-col gap-2" style={{ paddingBottom: 12 }}>
        {!partnerId ? (
          <p className="text-sm text-center mt-8" style={{ color: 'var(--cream-muted)' }}>
            Send a message to reach your coach.
          </p>
        ) : thread.isLoading ? (
          <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-center mt-8" style={{ color: 'var(--cream-muted)' }}>
            No messages yet. Say hello.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                <div
                  className="text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    maxWidth: '80%',
                    padding: '0.6rem 0.85rem',
                    borderRadius: 14,
                    background: mine ? 'var(--champagne)' : 'var(--surface-elevated)',
                    color: mine ? 'var(--ink)' : 'var(--cream-dim)',
                    borderBottomRightRadius: mine ? 4 : 14,
                    borderBottomLeftRadius: mine ? 14 : 4,
                  }}
                >
                  {m.content}
                </div>
                <span className="text-[10px] mt-1 px-1" style={{ color: 'var(--cream-muted)' }}>
                  {fmtTime(m.created_at)}
                </span>
              </div>
            );
          })
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate(draft.trim());
        }}
        className="flex gap-2 px-4 pt-3"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--ink-line, rgba(244,239,227,0.08))', background: 'var(--ink)' }}
      >
        <input
          className="input flex-1"
          placeholder="Message your coach…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Message"
        />
        <button type="submit" className="btn-gold px-5" disabled={send.isPending || !draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
